import { Clock, MapPin, User, ShieldCheck } from "lucide-react";

/**
 * ReadyToLeavePanel — small, calm, factual block for the daily-check
 * completion screen. Reduces "wait mode" anxiety by giving the instructor
 * the only three numbers that matter right now.
 *
 * Props:
 *  - lessonsToday: number
 *  - nextLesson:   { pupil_name, time, pickup, minutes_until } | null
 *  - earningsToday: number (£)
 *  - tone: "calm" (default) | "tight" — tight when leave-in <= 25 mins
 */
export default function ReadyToLeavePanel({
  lessonsToday = 0,
  nextLesson = null,
  earningsToday = 0,
  tone = "calm",
}) {
  // Empty state — clear diary
  if (lessonsToday === 0 || !nextLesson) {
    return (
      <div
        data-testid="ready-empty"
        className="rounded-2xl bg-slate-800/80 border border-slate-700 p-4 text-left"
      >
        <p className="text-[10px] font-extrabold uppercase tracking-wider text-emerald-400">
          Today is yours
        </p>
        <p className="mt-1 text-[13px] text-slate-100 font-bold">
          No lessons in the diary today.
        </p>
        <p className="text-[11px] text-slate-400 mt-1">
          Take a breath. Plan a slot or rest properly.
        </p>
      </div>
    );
  }

  const leaveIn = nextLesson?.minutes_until;
  const isTight = tone === "tight" || (leaveIn !== null && leaveIn !== undefined && leaveIn <= 25);

  return (
    <div
      data-testid="ready-summary"
      className={`rounded-2xl border p-4 text-left ${
        isTight
          ? "bg-amber-500/10 border-amber-500/40"
          : "bg-slate-800/80 border-slate-700"
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] font-extrabold uppercase tracking-wider text-emerald-400">
          Today is under control
        </p>
        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-400">
          <ShieldCheck className="w-3 h-3" />
          {lessonsToday} lesson{lessonsToday === 1 ? "" : "s"}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-2">
        <Row
          icon={Clock}
          label="First lesson"
          value={nextLesson.time || "—"}
          testid="ready-first-lesson"
        />
        <Row
          icon={User}
          label="Pupil"
          value={nextLesson.pupil_name || "Pupil"}
          testid="ready-pupil"
        />
        {nextLesson.pickup && (
          <Row
            icon={MapPin}
            label="Pickup"
            value={nextLesson.pickup}
            testid="ready-pickup"
          />
        )}
      </div>

      {leaveIn !== null && leaveIn !== undefined && (
        <div
          data-testid="ready-leave-in"
          className={`mt-3 pt-3 border-t flex items-center justify-between ${
            isTight ? "border-amber-500/30" : "border-slate-700"
          }`}
        >
          <p className={`text-[11px] font-extrabold uppercase tracking-wider ${isTight ? "text-amber-300" : "text-slate-400"}`}>
            Leave in
          </p>
          <p className={`text-2xl font-extrabold tabular-nums ${isTight ? "text-amber-300" : "text-white"}`}>
            {leaveIn} <span className="text-[12px] font-bold">mins</span>
          </p>
        </div>
      )}

      {earningsToday > 0 && (
        <p className="mt-2 text-[10px] text-slate-500 text-center">
          On track for <span className="font-extrabold text-slate-300">£{earningsToday.toFixed(2)}</span> today.
        </p>
      )}
    </div>
  );
}

function Row({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-slate-400 shrink-0" />
      <p className="text-[11px] font-bold text-slate-400 w-16 shrink-0">{label}</p>
      <p className="text-[13px] font-extrabold text-white truncate">{value}</p>
    </div>
  );
}
